/**
 * What: Defines shared product language for stable domain identifiers.
 * Transport / data-flow: Pure TypeScript constants and lookup helpers; product surfaces own component rendering.
 * Algorithm or layout: Domain-specific namespaces translate persisted identifiers into customer-facing language.
 */
import { productLanguageShopify } from './shopify.js';
import { shopifyHandoffCopy } from './shopify-handoff.js';
import { productLanguageIntegrationSettingsCopy } from './integration-settings.js';
import { shopperIntelligenceReviewCuesCopy } from './shopper-intelligence-review-cues.js';
import { studyResultsResponseThemes } from './study-results-response-themes.js';
export { launchAdmissionCopy } from './launch-admission.js';
export { shopifyComponentCopy } from './shopify-component-copy.js';
export { getReviewCueConfidenceLanguage, getReviewCueConvergenceLanguage, getReviewCueGroundingLanguage, getReviewCueProposalSourceLanguage, reviewCueConfidenceKeys, reviewCueConvergenceKeys, reviewCueGroundingKeys, reviewCueProposalSourceKeys, } from './shopper-intelligence-review-cues.js';
export { getShopifyAudienceModeLanguage, getShopifyPageLanguage, getShopifyReadinessReason, shopifyAudienceModeKeys, shopifyPageKeys, shopifyReadinessStatusKeys, } from './shopify.js';
export const studyResultTabKeys = ['overview', 'runs', 'arms', 'segments', 'response_themes', 'your_questions'];
export const studyResultVerdictBandKeys = ['strong', 'viable', 'iterate', 'weak', 'unknown'];
export const studyResultDecisionKeys = [
    'clear_winner',
    'directional_leader',
    'parity',
    'needs_more_n',
    'none_viable',
    'incomplete',
    'single_arm',
    'unknown',
];
export const studyResultIntentSkewKeys = ['high', 'middle', 'low', 'mixed', 'unknown'];
export const shopperSignalEvidenceKeys = ['browsed', 'purchased', 'both', 'unknown'];
export const shopperBackgroundCategoryKeys = [
    'purchase_history',
    'product_interests',
    'intent_signals',
    'audience_shape',
    'spend_history',
    'profile_traits',
];
export const integrationConnectionStatusKeys = ['active', 'needs_reconnect', 'disabled', 'not_connected'];
export const integrationLinkStatusKeys = ['linked', 'not_linked'];
export const shopifyMerchantPlanNames = {
    'free-v1': 'Free',
    'plus-v1': 'Plus',
};
const tabs = {
    overview: { key: 'overview', label: 'Overview' },
    runs: { key: 'runs', label: 'Previous results' },
    arms: { key: 'arms', label: 'Versions' },
    segments: { key: 'segments', label: 'Audience patterns' },
    response_themes: { key: 'response_themes', label: 'What shoppers said' },
    your_questions: { key: 'your_questions', label: 'Your questions' },
};
const verdictBands = {
    strong: { key: 'strong', label: 'Strong', tone: 'positive' },
    viable: { key: 'viable', label: 'Viable', tone: 'positive' },
    iterate: { key: 'iterate', label: 'Iterate', tone: 'caution' },
    weak: { key: 'weak', label: 'Weak', tone: 'negative' },
    unknown: { key: 'unknown', label: 'Unknown', tone: 'neutral' },
};
const decisions = {
    clear_winner: { key: 'clear_winner', label: 'Clear winner', tone: 'positive' },
    directional_leader: { key: 'directional_leader', label: 'Leaning toward a version', tone: 'caution' },
    parity: { key: 'parity', label: 'Top versions are close', tone: 'neutral' },
    needs_more_n: { key: 'needs_more_n', label: 'Not enough responses', tone: 'caution' },
    none_viable: { key: 'none_viable', label: 'All versions need work', tone: 'caution' },
    incomplete: { key: 'incomplete', label: 'Comparison incomplete', tone: 'neutral' },
    single_arm: { key: 'single_arm', label: 'Single-version read', tone: 'neutral' },
    unknown: { key: 'unknown', label: 'Result unavailable', tone: 'neutral' },
};
const intentSkews = {
    high: { key: 'high', label: 'High purchase interest', tone: 'positive' },
    middle: { key: 'middle', label: 'Some purchase interest', tone: 'caution' },
    low: { key: 'low', label: 'Low purchase interest', tone: 'negative' },
    mixed: { key: 'mixed', label: 'Mixed intent', tone: 'neutral' },
    unknown: { key: 'unknown', label: 'Unknown intent', tone: 'neutral' },
};
const shopperSignalEvidence = {
    browsed: { key: 'browsed', label: 'Browsed' },
    purchased: { key: 'purchased', label: 'Purchased' },
    both: { key: 'both', label: 'Browsed + purchased' },
    unknown: { key: 'unknown', label: 'Observed' },
};
const shopperBackgroundCategories = {
    purchase_history: { key: 'purchase_history', label: 'Purchase history' },
    product_interests: { key: 'product_interests', label: 'Product interests' },
    intent_signals: { key: 'intent_signals', label: 'High-intent paths' },
    audience_shape: { key: 'audience_shape', label: 'Audience shape' },
    spend_history: { key: 'spend_history', label: 'Spend background' },
    profile_traits: { key: 'profile_traits', label: 'Profile traits' },
};
const integrationConnectionStatuses = {
    active: { key: 'active', label: 'Active', tone: 'positive' },
    needs_reconnect: { key: 'needs_reconnect', label: 'Needs reconnect', tone: 'negative' },
    disabled: { key: 'disabled', label: 'Disabled', tone: 'neutral' },
    not_connected: { key: 'not_connected', label: 'Not connected', tone: 'neutral' },
};
const integrationLinkStatuses = {
    linked: { key: 'linked', label: 'Linked', tone: 'positive' },
    not_linked: { key: 'not_linked', label: 'Not linked', tone: 'caution' },
};
export const productLanguage = {
    shopify: productLanguageShopify,
    shopifyHandoff: shopifyHandoffCopy,
    studyResults: {
        tabs,
        verdictBands,
        decisions,
        intentSkews,
        overview: {
            outcomeHeading: 'Decision summary',
            outcomeDescription: 'What shoppers preferred in this test, with a clear next step.',
            recommendationHeading: 'Recommendation',
            unavailableRange: 'Range unavailable',
            unavailableSample: 'Sample unavailable',
            selectedArmFallback: 'Selected version',
            noVerdict: 'No verdict available for this run.',
            metricLabels: {
                t2b: 'Likely buyer share',
                decision: 'Decision',
                personas: 'Simulated shoppers',
            },
            metricHelp: {
                t2b: 'The share of simulated shoppers who showed likely interest in buying.',
                decision: 'A plain-language summary of how the tested options compared.',
                personas: 'Number of simulated shoppers included in this test.',
            },
            decisionDetailJoiner: 'and',
            parityDetailFallback: 'Tested options',
        },
        responseThemes: studyResultsResponseThemes,
        tables: {
            runsHeadings: ['Result', 'Status', 'Requested', 'Finished'],
            armsHeadings: ['Version', 'Likely buyer share', 'Directional range', 'Verdict'],
            treatmentImageAlt: 'Version image',
            selectedTreatment: 'Selected',
            baselineRole: 'Baseline',
            treatmentRole: 'Version',
            pricePrefix: 'Price',
            unavailableValue: 'n/a',
            detailsUnavailable: 'Detailed test results are not yet available for this run.',
        },
    },
    shopperIntelligence: {
        signals: {
            heading: 'Shopper Intelligence Signals',
            coverageHeading: 'Coverage',
            sourceJoiner: ' · ',
            emptyState: 'No shopper intelligence signals are available yet.',
            sparseCoverage: 'Limited browsing coverage',
            sourceLabels: {
                posthogBrowsing: 'PostHog browsing',
                shopifyPurchases: 'Shopify purchases',
            },
            caveat: 'Browsing signals are aggregate shopper background only and are not real-customer targeting segments.',
            metricTemplates: {
                purchasedCustomersBrowsedVisitors: 'customers purchased · visitors browsed',
                visitorsBrowsed: 'visitors browsed',
                customersPurchased: 'customers purchased',
                visitorsEvents: 'visitors · events',
                visitorsShare: 'visitors',
            },
        },
        evidence: shopperSignalEvidence,
        backgroundCategories: shopperBackgroundCategories,
        reviewCues: shopperIntelligenceReviewCuesCopy,
        audienceLevers: {
            browsedProductPrefix: 'Browsed product',
            purchasedProductPrefix: 'Purchased product',
            productInterestPrefix: 'Product interest',
            highIntentPathPrefix: 'High-intent path',
            deviceContextPrefix: 'Device context',
            geoContextPrefix: 'Geo context',
            sourceChannelContextPrefix: 'Source channel',
            browsingPreview: 'This audience was directionally shaped using observed product browsing.',
            purchasePreview: 'This audience was directionally shaped using observed product purchases.',
            purchaseClaimBoundary: 'Purchase patterns describe shopper background, not customer targeting or a sales forecast.',
            highIntentPreview: 'This audience was directionally shaped using observed high-intent browsing.',
            devicePreview: 'This audience was directionally shaped using observed device context.',
            geoPreview: 'This audience was directionally shaped using observed location context.',
            sourceChannelPreview: 'This audience was directionally shaped using observed source-channel context.',
            browsingClaimBoundary: 'Browsing signals are aggregate shopper background only, not real-customer targeting segments.',
            deviceClaimBoundary: 'Device signals are aggregate shopper background only, not real-customer targeting segments.',
            geoClaimBoundary: 'Location signals are aggregate shopper background only, not real-customer targeting segments.',
            sourceChannelClaimBoundary: 'Source-channel signals are aggregate shopper background only, not real-customer targeting segments.',
        },
    },
    integrationSettings: {
        ...productLanguageIntegrationSettingsCopy,
        connectionStatuses: integrationConnectionStatuses,
        linkStatuses: integrationLinkStatuses,
    },
};
/** Returns customer-facing language for a study-result tab key. */
export function getStudyResultTabLanguage(key) {
    return productLanguage.studyResults.tabs[key];
}
/** Returns customer-facing language for persisted study-result verdict-band identifiers. */
export function getStudyResultVerdictBandLanguage(key) {
    if (isStudyResultVerdictBandKey(key)) {
        return productLanguage.studyResults.verdictBands[key];
    }
    return productLanguage.studyResults.verdictBands.unknown;
}
/** Returns customer-facing language for persisted study-result decision identifiers. */
export function getStudyResultDecisionLanguage(key) {
    if (isStudyResultDecisionKey(key)) {
        return productLanguage.studyResults.decisions[key];
    }
    return productLanguage.studyResults.decisions.unknown;
}
/** Returns merchant-facing evidence language for structured recommendation evidence keys. */
export function getStudyResultEvidenceLanguage(key) {
    if (key === 'below_viability_threshold')
        return 'Below viability threshold';
    return null;
}
/** Returns the headline verdict language implied by the parent decision and arm data. */
export function getStudyResultHeadlineVerdictBandLanguage(decision, armVerdictBand) {
    if (isStudyResultDecisionKey(decision) && decision !== 'single_arm' && decision !== 'unknown') {
        return productLanguage.studyResults.decisions[decision];
    }
    return getStudyResultVerdictBandLanguage(armVerdictBand);
}
/** Returns a decision-owned recommendation when the arm-level verdict is not authoritative. */
export function getStudyResultDecisionRecommendation(decision, armRecommendation, recommendedAction) {
    if (decision === 'parity' || recommendedAction === 'treat_as_parity') {
        return 'This test gives no clear reason to prefer one of the leading versions. Consider margin, brand fit or production cost.';
    }
    if (decision === 'none_viable') {
        return 'Every version scored below this test’s threshold. Rework the options before the next test.';
    }
    if (decision === 'needs_more_n') {
        return 'More responses are needed before choosing between these versions.';
    }
    if (decision === 'incomplete') {
        return 'Complete the missing version results before making a recommendation.';
    }
    if (decision === 'clear_winner')
        return 'The highlighted version leads clearly in this test. Use it for the next step.';
    if (decision === 'directional_leader')
        return 'The highlighted version leads, but the result is not decisive.';
    return armRecommendation ?? 'Review the test results before choosing the next step.';
}
/** Formats the treatments involved in a decision without implying parity has a winner. */
export function formatStudyResultDecisionDetail(decision, primaryLabel, secondaryLabel) {
    if (decision === 'parity' && secondaryLabel) {
        return `${primaryLabel} ${productLanguage.studyResults.overview.decisionDetailJoiner} ${secondaryLabel}`;
    }
    if (decision === 'parity')
        return productLanguage.studyResults.overview.parityDetailFallback;
    return primaryLabel;
}
/** Returns customer-facing language for response-theme intent skew identifiers. */
export function getStudyResultIntentSkewLanguage(key) {
    if (isStudyResultIntentSkewKey(key)) {
        return productLanguage.studyResults.intentSkews[key];
    }
    return productLanguage.studyResults.intentSkews.unknown;
}
/** Returns customer-facing language for a persisted Shopper Intelligence evidence identifier. */
export function getShopperSignalEvidenceLanguage(key) {
    if (isShopperSignalEvidenceKey(key)) {
        return productLanguage.shopperIntelligence.evidence[key];
    }
    return productLanguage.shopperIntelligence.evidence.unknown;
}
/** Returns customer-facing language for a Shopper Background category identifier. */
export function getShopperBackgroundCategoryLanguage(key) {
    if (isShopperBackgroundCategoryKey(key)) {
        return productLanguage.shopperIntelligence.backgroundCategories[key];
    }
    return productLanguage.shopperIntelligence.backgroundCategories.profile_traits;
}
/** Returns customer-facing language for integration connection status identifiers. */
export function getIntegrationConnectionStatusLanguage(key) {
    if (isIntegrationConnectionStatusKey(key))
        return productLanguage.integrationSettings.connectionStatuses[key];
    return productLanguage.integrationSettings.connectionStatuses.not_connected;
}
/** Returns customer-facing language for store-to-source link status identifiers. */
export function getIntegrationLinkStatusLanguage(key) {
    if (isIntegrationLinkStatusKey(key))
        return productLanguage.integrationSettings.linkStatuses[key];
    return productLanguage.integrationSettings.linkStatuses.not_linked;
}
/** Formats a product interest label while keeping the stable prefix in product language. */
export function formatShopperProductInterestLabel(productName) {
    return `${productLanguage.shopperIntelligence.audienceLevers.productInterestPrefix}: ${productName}`;
}
/** Formats a browsed-product audience lever label with package-owned product language. */
export function formatShopperBrowsedProductLeverLabel(productName) {
    return `${productLanguage.shopperIntelligence.audienceLevers.browsedProductPrefix}: ${productName}`;
}
/** Formats a high-intent path audience lever label with package-owned product language. */
export function formatShopperHighIntentPathLeverLabel(pathName) {
    return `${productLanguage.shopperIntelligence.audienceLevers.highIntentPathPrefix}: ${pathName}`;
}
/** Formats a device-context audience lever label with package-owned product language. */
export function formatShopperDeviceContextLeverLabel(deviceName) {
    return `${productLanguage.shopperIntelligence.audienceLevers.deviceContextPrefix}: ${deviceName}`;
}
/** Formats a geo-context audience lever label with package-owned product language. */
export function formatShopperGeoContextLeverLabel(value) {
    return `${productLanguage.shopperIntelligence.audienceLevers.geoContextPrefix}: ${value}`;
}
/** Formats a source-channel audience lever label with package-owned product language. */
export function formatShopperSourceChannelContextLeverLabel(value) {
    return `${productLanguage.shopperIntelligence.audienceLevers.sourceChannelContextPrefix}: ${value}`;
}
/** Formats product-interest evidence that has both purchase and browsing support. */
export function formatShopperPurchasedBrowsedMetric(params) {
    return `${params.purchasedCustomers.toLocaleString()} customers purchased · ${params.browsedVisitors.toLocaleString()} visitors browsed`;
}
/** Formats browsing-only product-interest evidence. */
export function formatShopperVisitorsBrowsedMetric(visitors) {
    return `${visitors.toLocaleString()} visitors browsed`;
}
/** Formats high-intent path activity evidence. */
export function formatShopperVisitorsEventsMetric(params) {
    return `${params.visitors.toLocaleString()} visitors · ${params.events.toLocaleString()} events`;
}
/** Formats aggregate audience-shape share evidence. */
export function formatShopperVisitorsShareMetric(params) {
    return `${params.percentage.toLocaleString()}% · ${params.visitors.toLocaleString()} visitors`;
}
export * from './shopify-component-formatters.js';
function isStudyResultVerdictBandKey(key) {
    return studyResultVerdictBandKeys.includes(key);
}
/** Whether a stored decision string is one of the known study-result decision keys. */
export function isStudyResultDecisionKey(key) {
    return studyResultDecisionKeys.includes(key);
}
function isStudyResultIntentSkewKey(key) {
    return studyResultIntentSkewKeys.includes(key);
}
function isShopperSignalEvidenceKey(key) {
    return shopperSignalEvidenceKeys.includes(key);
}
function isShopperBackgroundCategoryKey(key) {
    return shopperBackgroundCategoryKeys.includes(key);
}
function isIntegrationConnectionStatusKey(key) {
    return integrationConnectionStatusKeys.includes(key);
}
function isIntegrationLinkStatusKey(key) {
    return integrationLinkStatusKeys.includes(key);
}
export { shopifyIndexCopy } from './shopify-index.js';
export { insightsLoadingCopy } from './insights-loading.js';
export { shopifyAppLoadingCopy } from './shopify-app-loading.js';
export { insightOpportunityCopy } from './insight-opportunities.js';
export { storeKnowledgeCopy } from './store-knowledge.js';
export { responsePhraseCopy } from './response-phrases.js';
export { nextTestsCopy } from './study-next-tests.js';
export { getStudyRunProgressHeadline, getStudyNodeProgressLines, getStudyRunFailureMessage, } from './study-results-progress.js';
export * from './results-respondents.js';
//# sourceMappingURL=index.js.map